"use client"
import Image from 'next/image'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import type { Post } from '@/lib/api'
import { useEffect, useState, useCallback } from 'react'
import { getCategoryColor } from '@/lib/constants'
import { useVideoModal } from './VideoModalProvider'

type HeroSliderProps = {
  posts: Post[]
  translations: Record<string, string>
}

export default function HeroSlider({ posts, translations }: HeroSliderProps) {
  const locale = useLocale()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const { openModal } = useVideoModal()
  const [direction, setDirection] = useState<'next' | 'prev'>('next')

  // Navigation infinie sans limite
  const goToNext = useCallback(() => {
    setDirection('next')
    setCurrentIndex((prevIndex) => (prevIndex + 1) % posts.length)
  }, [posts.length])

  const goToPrev = useCallback(() => {
    setDirection('prev')
    setCurrentIndex((prevIndex) => (prevIndex - 1 + posts.length) % posts.length)
  }, [posts.length])

  const goToSlide = useCallback((index: number) => {
    setDirection(index > currentIndex ? 'next' : 'prev')
    setCurrentIndex(index)
  }, [currentIndex])

  // Auto-play avec pause au survol
  useEffect(() => {
    if (isPaused || posts.length <= 1) return
    
    const interval = setInterval(goToNext, 6000)
    return () => clearInterval(interval)
  }, [isPaused, posts.length, goToNext])

  // Support du clavier (accessibilité)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrev])

  if (!posts.length) return null

  const current = posts[currentIndex]
  const colors = getCategoryColor(current.category)
  
  // Fonction pour tronquer intelligemment la description
  const getOptimalExcerpt = (text: string, maxChars: number = 120) => {
    if (!text || text.length <= maxChars) return text
    
    // Troncature intelligente aux mots pour éviter les coupures
    const words = text.split(' ')
    let result = ''
    
    for (const word of words) {
      if ((result + ' ' + word).length > maxChars) break
      result += (result ? ' ' : '') + word
    }
    
    return result + (result.length < text.length ? '...' : '')
  }
  const href = `/${locale}/${current.category}/${current.slug}`

  return (
    <section 
      aria-label="Articles à la une" 
      className="relative h-[400px] sm:h-[500px] lg:h-[600px] overflow-hidden bg-black"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Image principale avec transition */}
      <div className="relative h-full w-full">
        {posts.map((post, i) => (
          <div
            key={post.id}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              i === currentIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <Image
              src={post.coverImage}
              alt={post.title}
              fill
              priority={i === 0}
              className="object-cover"
              sizes="100vw"
            />
          </div>
        ))}
      </div>

      {/* Indicateur vidéo si applicable */}
      {current.type === 'video' && (
        <>
          {/* Badge "VIDÉO" en haut à gauche */}
          <div className="absolute top-6 left-6 z-10 flex items-center gap-2 rounded bg-red-600 px-3 py-2 text-sm font-bold text-white shadow-lg">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            VIDÉO
          </div>
          
          {/* Bouton de lecture YouTube au centre */}
          <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (current.youtubeId) {
                  openModal(current.youtubeId!, current.title)
                }
              }}
              onTouchStart={(e) => {
                // Empêche le double-tap sur mobile
                e.stopPropagation()
              }}
              className="flex h-20 w-20 items-center justify-center rounded-full bg-red-600 text-white shadow-xl transition-all hover:bg-red-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-red-500/50 active:scale-95 pointer-events-auto"
              aria-label="Regarder la vidéo sur YouTube"
            >
              <svg className="h-10 w-10 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </button>
          </div>
        </>
      )}

      {/* Overlay gradient plus prononcé */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
      
      {/* Contenu */}
      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12 pointer-events-auto z-40">
        <div className="mx-auto max-w-7xl">
          <div className="group">
            {/* Badge catégorie avec couleur dynamique */}
            <span className={`mb-3 inline-block ${colors.bg} px-3 py-1 text-xs font-bold uppercase tracking-wider text-white`}>
              {translations[current.category] || current.category}
            </span>
            
            {/* Titre cliquable vers les détails */}
            <Link 
              href={href}
              className="block relative"
              style={{
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                zIndex: 50,
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.01)'
                const h2 = e.currentTarget.querySelector('h2')
                if (h2) {
                  h2.style.color = '#e5e7eb'
                  h2.style.textShadow = '0 2px 8px rgba(255, 255, 255, 0.3)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                const h2 = e.currentTarget.querySelector('h2')
                if (h2) {
                  h2.style.color = 'white'
                  h2.style.textShadow = 'none'
                }
              }}
            >
              <h2 
                className="mb-3 max-w-4xl text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                {current.title}
              </h2>
            </Link>
            
            {/* Extrait optimisé visible sur desktop */}
            <p className="hidden max-w-2xl text-lg text-gray-200 lg:block leading-relaxed">
              {getOptimalExcerpt(current.excerpt, 120)}
            </p>
            
            {/* Meta info */}
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-300">
              {current.author && <span>{current.author}</span>}
              <span>{new Date(current.publishedAt).toLocaleDateString(locale)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation flèches - Style Noovo inspiré */}
      <button
        onClick={goToPrev}
        aria-label="Article précédent"
        className="group absolute left-6 top-1/2 z-50 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 lg:h-14 lg:w-14"
        disabled={posts.length <= 1}
      >
        <svg 
          className="h-5 w-5 transition-transform group-hover:-translate-x-1 lg:h-6 lg:w-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button
        onClick={goToNext}
        aria-label="Article suivant"
        className="group absolute right-6 top-1/2 z-50 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 lg:h-14 lg:w-14"
        disabled={posts.length <= 1}
      >
        <svg 
          className="h-5 w-5 transition-transform group-hover:translate-x-1 lg:h-6 lg:w-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Indicateurs (dots) améliorés avec compteur */}
      <div className="absolute bottom-6 right-6 flex items-center gap-3 sm:right-8 lg:right-12">
        {/* Compteur actuel */}
        <span className="text-sm font-medium text-white/80">
          {currentIndex + 1} / {posts.length}
        </span>
        
        {/* Dots interactifs */}
        <div className="flex items-center gap-2">
          {posts.map((_, i) => (
            <button
              key={i}
              aria-label={`Aller au slide ${i + 1}`}
              className={`transition-all duration-300 ${
                i === currentIndex 
                  ? 'h-2 w-8 bg-white' 
                  : 'h-2 w-2 bg-white/50 hover:bg-white/70 hover:scale-125'
              } rounded-full focus:outline-none focus:ring-2 focus:ring-white/50`}
              onClick={() => goToSlide(i)}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails des autres posts (desktop) - Navigation rapide */}
      <div className="absolute right-0 top-20 z-30 hidden w-80 space-y-2 p-4 lg:block">
        {posts.slice(0, 4).map((post, i) => {
          if (i === currentIndex) return null
          const href = `/${locale}/${post.category}/${post.slug}`
          return (
            <Link
              key={post.id}
              href={href}
              onClick={(e) => {
                console.log('Thumbnail clicked:', post.title, href)
              }}
              className="group flex w-full gap-3 rounded bg-black/50 p-2 backdrop-blur transition-all duration-300 hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              <div className="relative h-16 w-24 flex-shrink-0 overflow-hidden rounded">
                <Image
                  src={post.coverImage}
                  alt={post.title}
                  fill
                  className="object-cover transition-transform group-hover:scale-110"
                  sizes="96px"
                />
                {/* Badge vidéo si applicable */}
                {post.type === 'video' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-red-600 p-1">
                      <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 text-left">
                <h3 className="line-clamp-2 text-sm font-semibold text-white group-hover:text-gray-200">
                  {post.title}
                </h3>
                <p className="mt-1 text-xs text-gray-400">
                  {new Date(post.publishedAt).toLocaleDateString(locale)}
                </p>
              </div>
            </Link>
          )
        })}
      </div>


    </section>
  )
}

